import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { Menu, Button, Layout, theme } from 'antd';
const { Sider } = Layout;

const Sidebar = ({ isCollapsed, onToggle }) => {
  const [openKeys, setOpenKeys] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();

  const navigationItems = useMemo(() => [
    {
      key: 'dashboard',
      id: 'dashboard',
      label: 'Dashboard',
      icon: 'LayoutDashboard',
      path: '/dashboard',
      requiredPermissions: []
    },
    {
      key: 'my-account',
      id: 'my-account',
      label: 'My Account',
      icon: 'User',
      children: [
        {
          key: 'profile-settings',
          id: 'profile-settings',
          label: 'Profile Settings',
          icon: 'Settings',
          path: '/profile',
          requiredPermissions: []
        }
      ]
    },
    {
      key: 'administration',
      id: 'administration',
      label: 'Administration',
      icon: 'ShieldCheck',
      children: [
        {
          key: 'user-management',
          id: 'user-management',
          label: 'User Management',
          icon: 'UserCog',
          path: '/users',
          requiredPermissions: ['admin', 'user_management']
        },
        {
          key: 'user-registration',
          id: 'user-registration',
          label: 'User Registration',
          icon: 'UserPlus',
          path: '/register',
          requiredPermissions: ['admin', 'user_management']
        },
        {
          key: 'role',
          id: 'role',
          label: 'Role & Permission Management',
          icon: 'Key',
          path: '/role',
          requiredPermissions: ['admin', 'security_management']
        },
        {
          key: 'session',
          id: 'session',
          label: 'Session Management',
          icon: 'Activity',
          path: '/session',
          requiredPermissions: ['admin', 'security_management']
        }
      ]
    },
    {
      key: 'localization',
      id: 'localization',
      label: 'Localization',
      icon: 'Languages',
      path: '/localization',
      requiredPermissions: ['admin', 'user_management']
    },
    {
      key: 'game-content',
      id: 'game-content',
      label: 'Game Content',
      icon: 'Gamepad2',
      children: [
        {
          key: 'content-entity',
          id: 'content-entity',
          label: 'Entity',
          icon: 'ScanBox',
          path: '/content/entity',
          requiredPermissions: ['admin', 'user_management']
        },
        {
          key: 'content-parameter',
          id: 'content-parameter',
          label: 'Parameter',
          icon: 'ScanSquare',
          path: '/content/parameters',
          requiredPermissions: ['admin', 'user_management']
        },
        {
          key: 'content-group-parameter',
          id: 'content-group-parameter',
          label: 'Group Parameter',
          icon: 'Group',
          path: '/content/group-parameters',
          requiredPermissions: ['admin', 'user_management']
        }
      ]
    },
    {
      key: 'projects',
      id: 'projects',
      label: 'Projects',
      icon: 'Folder',
      children: [
        {
          key: 'projects-list',
          id: 'projects-list',
          label: 'Projects List',
          path: '/projects/list',
          icon: 'FolderOpen'
        },
        {
          key: 'projects-versions',
          id: 'projects-versions',
          label: 'Versions',
          path: '/projects/versions',
          icon: 'Layers'
        }
      ],
      requiredPermissions: ['admin', 'user_management']
    }
  ], []);

  const keyToPathMap = useMemo(() => {
    const map = new Map();

    navigationItems.forEach(item => {
      if (item.path) {
        map.set(item.key, item.path);
      }

      if (item.children) {
        item.children.forEach(child => {
          if (child.path) {
            map.set(child.key, child.path);
          }
        });
      }
    });

    return map;
  }, [navigationItems]);

  const submenuKeys = useMemo(() => {
    return navigationItems
      .filter(item => item.children?.length)
      .map(item => item.key);
  }, [navigationItems]);

  const selectedKey = useMemo(() => {
    for (const item of navigationItems) {
      if (item.path && item.path === location.pathname) {
        return item.key;
      }

      if (item.children) {
        const activeChild = item.children.find(child => child.path === location.pathname);
        if (activeChild) {
          return activeChild.key;
        }
      }
    }

    return undefined;
  }, [location.pathname, navigationItems]);

  useEffect(() => {
    const parentKeys = navigationItems
      .filter(item => item.children?.some(child => child.path === location.pathname))
      .map(item => item.key);

    if (parentKeys.length > 0) {
      setOpenKeys(prev => Array.from(new Set([...prev, ...parentKeys])));
    }
  }, [location.pathname, navigationItems]);

  useEffect(() => {
    if (isCollapsed) {
      setOpenKeys([]);
    }
  }, [isCollapsed]);

  const menuItems = useMemo(() => {
    return navigationItems.map(item => {
      const itemIcon = <Icon name={item.icon} size={18} />;

      if (item.children?.length) {
        return {
          key: item.key,
          icon: itemIcon,
          label: item.label,
          children: item.children.map(child => ({
            key: child.key,
            icon: <Icon name={child.icon} size={16} />,
            label: child.label
          }))
        };
      }

      return {
        key: item.key,
        icon: itemIcon,
        label: item.label
      };
    });
  }, [navigationItems]);

  const handleNavigation = (path) => {
    if (path) {
      navigate(path);
    }
  };

  const handleMenuClick = ({ key }) => {
    const targetPath = keyToPathMap.get(key);
    if (targetPath) {
      handleNavigation(targetPath);
    }
  };

  const handleOpenChange = (nextOpenKeys) => {
    setOpenKeys(nextOpenKeys.filter(key => submenuKeys.includes(key)));
  };

  const isActiveItem = (path) => {
    return location.pathname === path;
  };

  const isActiveSectionItem = (children) => {
    return children?.some(child => location.pathname === child.path);
  };
  const {
    token: { colorBgContainer, borderRadiusLG, colorBgLayout, colorBgBlur, colorBgSolidHover },
  } = theme.useToken();
  return (
    <>
      {/* Desktop Sidebar */}
      <Sider style={{ background: colorBgBlur }} className="" collapsible collapsed={isCollapsed}  onCollapse={onToggle} >
        {/* Logo Header */}
        <div style={{background: colorBgBlur}} className="h-16 flex items-center justify-between p-4 ">
          {!isCollapsed && (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-primary rounded-md flex items-center justify-center">
                <Icon name="Shield" size={20} className="text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-text-primary">SbAuthHub</h1>
                <p className="text-xs text-text-secondary">Enterprise System</p>
              </div>
            </div>
          )}
          <Button
            type="text"
            onClick={onToggle}
            className="rounded-md hover:bg-secondary-50 transition-colors duration-150 p-4"
            icon={<Icon name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={16} className="text-secondary-500" />}
          >
          </Button>
        </div>

        {/* Navigation */}
        <Menu
            style={{ background: colorBgBlur, border: 'none' }}
            mode="inline"
            inlineCollapsed={isCollapsed}
            items={menuItems}
            onClick={handleMenuClick}
            selectedKeys={selectedKey ? [selectedKey] : []}
            openKeys={openKeys}
            onOpenChange={handleOpenChange}
          />
      </Sider>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-surface border-t border-border z-sidebar">
        <div className="flex items-center justify-around h-nav-mobile px-2">
          {navigationItems.slice(0, 4).map(item => {
            const isActive = isActiveItem(item.path) || isActiveSectionItem(item.children);
            const mainPath = item.children?.[0]?.path || item.path;

            return (
              <button
                key={item.id}
                onClick={() => mainPath && handleNavigation(mainPath)}
                className={`
                  flex flex-col items-center justify-center p-2 rounded-md min-w-0 flex-1
                  transition-all duration-150 ease-out
                  ${isActive
                    ? 'text-primary bg-primary-50' : 'text-secondary-500 hover:text-primary-600'
                  }
                `}
              >
                <Icon name={item.icon} size={20} className="mb-1" />
                <span className="text-xs font-medium truncate">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};

export default Sidebar;
